import { KeyboardEvent, useState } from 'react';

import useRestoredFocusRing from './useRestoredFocusRing';

interface IOptions {
  value: Date | null;
  onCommit: (value: Date | null) => void;
  disabled?: boolean;
}

const isSameValue = (a: Date | null, b: Date | null) => a?.getTime() === b?.getTime();

export const isPickerOpenKey = (event: KeyboardEvent) =>
  event.key === 'Enter' || event.key === ' ' || (event.altKey && event.key === 'ArrowDown');

const usePickerDraft = ({ value, onCommit, disabled }: IOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<Date | null>(null);
  const focusRing = useRestoredFocusRing();

  const open = () => {
    if (disabled) return;
    setDraft(value);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    focusRing.onPopupClose();
  };

  const accept = () => {
    close();
    if (!isSameValue(draft, value)) onCommit(draft);
  };

  const onFieldKeyDownCapture = (event: KeyboardEvent) => {
    if (event.key === 'Tab') return;
    event.preventDefault();
    event.stopPropagation();
    if (isPickerOpenKey(event)) open();
  };

  return {
    actions: { cancel: close, accept },
    pickerProps: {
      value: isOpen ? draft : value,
      onChange: setDraft,
      open: isOpen,
      onOpen: open,
      onClose: close,
    },
    textFieldProps: {
      onClick: open,
      onKeyDownCapture: onFieldKeyDownCapture,
      onBlur: focusRing.onFieldBlur,
      focused: focusRing.fieldFocused,
    },
    dialogProps: { onClose: close, ...focusRing.popupHandlers },
  };
};

export default usePickerDraft;
